import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { storeOrganizationData } from './organization-utils';
import { baseUrl, openBaseUrl, hostName } from '../../app.config';
import { Amplify } from 'aws-amplify';

@Injectable({ providedIn: 'root' })
export class OrganizationInitializerService {
    private _httpClient = inject(HttpClient);

    /**
     * Initialize organization data before app bootstrap
     */
    async initializeOrganization(): Promise<void> {
        try {
            // Try to get organization details from API
            const response = await firstValueFrom(
                this._httpClient.get<any>(
                    `${openBaseUrl}/tenantmaster/details?url=https://${hostName}`,
                    {}
                )
            );

            if (response?.data) {
                const orgData = response.data;
                
                // Store organization data in localStorage
                storeOrganizationData({
                    name: orgData.organization_name || '',
                    logo: orgData.employee_app_icon || ''
                });

                // Store additional organization data for app functionality
                localStorage.setItem('user_pool_id', orgData.user_pool_id);
                localStorage.setItem('tenant_id', orgData.tenant_id);
                localStorage.setItem('login_type', orgData.login_username_type);
                localStorage.setItem('retail_yn', orgData.retail_yn);
                localStorage.setItem('margin_yn', orgData.margin_yn);
                localStorage.setItem('organisation', JSON.stringify(orgData));

                // Configure Amplify with organization data
                this.configureAmplify(orgData);

                // Update splash screen logo immediately
                this.updateSplashScreenLogo(orgData.employee_app_icon);
                
                // Update document title immediately
                this.updateDocumentTitle(orgData.organization_name);
            }
        } catch (error) {
            console.warn('Failed to load organization data:', error);
            
            // Try to use cached data if available
            const cachedOrgName = localStorage.getItem('orgName');
            const cachedOrgLogo = localStorage.getItem('orgLogo');
            
            if (cachedOrgName) {
                this.updateDocumentTitle(cachedOrgName);
            }
            
            if (cachedOrgLogo) {
                this.updateSplashScreenLogo(cachedOrgLogo);
            }
        }
    }

    /**
     * Update splash screen logo with organization logo
     */
    private updateSplashScreenLogo(logoUrl: string): void {
        if (logoUrl) {
            const splashScreenImg = document.querySelector('fuse-splash-screen img') as HTMLImageElement;
            if (splashScreenImg) {
                splashScreenImg.src = logoUrl;
                splashScreenImg.alt = 'organization logo';
                splashScreenImg.style.display = 'block';
            }
        }
    }

    /**
     * Update document title with organization name
     */
    private updateDocumentTitle(orgName: string): void {
        if (orgName) {
            document.title = orgName;
        }
    }

    /**
     * Configure Amplify with organization data
     */
    private configureAmplify(orgData: any): void {
        if (orgData.user_pool_id && orgData.app_client_admin) {
            Amplify.configure({
                Auth: {
                    Cognito: {
                        userPoolId: orgData.user_pool_id,
                        userPoolClientId: orgData.app_client_admin,
                    }
                }
            });
        }
    }
}
